import { useState } from "react";
import { ASSA_TOKEN, BASE_CHAIN, ASSA_BASESCAN } from "../../lib/token";

/**
 * $ASSA contract address + "Add to MetaMask" (EIP-747 wallet_watchAsset).
 *
 * watchAsset imports the token as a USER-ADDED asset, so the wallet shows it
 * even at a 0 balance (MetaMask's "hide zero-balance" only hides auto-detected
 * tokens). Passing `image` also gives the logo immediately — no token-list /
 * CoinGecko listing required for users who add via this button.
 */

// Minimal EIP-1193 shape — we only call request(). Accessed via a cast so we
// don't globally augment Window (avoids clashes with wagmi/viem typings).
interface Eip1193Provider {
  request(args: { method: string; params?: unknown }): Promise<unknown>;
  isMetaMask?: boolean;
}
function getEthereum(): Eip1193Provider | undefined {
  return (window as unknown as { ethereum?: Eip1193Provider }).ethereum;
}

type Status = "idle" | "working" | "added" | "rejected" | "nowallet" | "error";

const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

export default function TokenActions() {
  const [status, setStatus] = useState<Status>("idle");
  const [copied, setCopied] = useState(false);

  async function ensureBase(eth: Eip1193Provider) {
    try {
      await eth.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: BASE_CHAIN.chainIdHex }],
      });
    } catch (e) {
      // 4902 = chain not present in the wallet → add it (this also selects it).
      if ((e as { code?: number })?.code === 4902) {
        await eth.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainId: BASE_CHAIN.chainIdHex,
              chainName: BASE_CHAIN.chainName,
              nativeCurrency: BASE_CHAIN.nativeCurrency,
              rpcUrls: BASE_CHAIN.rpcUrls,
              blockExplorerUrls: BASE_CHAIN.blockExplorerUrls,
            },
          ],
        });
      } else {
        throw e;
      }
    }
  }

  async function addToWallet() {
    const eth = getEthereum();
    if (!eth) {
      setStatus("nowallet");
      window.open("https://metamask.io/download/", "_blank", "noopener,noreferrer");
      return;
    }
    setStatus("working");
    try {
      await ensureBase(eth);
      const ok = (await eth.request({
        method: "wallet_watchAsset",
        params: {
          type: "ERC20",
          options: {
            address: ASSA_TOKEN.address,
            symbol: ASSA_TOKEN.symbol,
            decimals: ASSA_TOKEN.decimals,
            image: ASSA_TOKEN.image,
          },
        },
      })) as boolean;
      setStatus(ok ? "added" : "rejected");
    } catch (e) {
      setStatus((e as { code?: number })?.code === 4001 ? "rejected" : "error");
    }
  }

  async function copyAddress() {
    try {
      await navigator.clipboard.writeText(ASSA_TOKEN.address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the address is shown in full anyway */
    }
  }

  const label =
    status === "working" ? "Adding…" : status === "added" ? "Added ✓" : "Add $ASSA to MetaMask";

  return (
    <div className="mt-12">
      {/* Contract address (Base) */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px]">
        <span className="text-ink-soft text-[12px] font-semibold uppercase tracking-[0.08em]">
          Contract · Base
        </span>
        <code className="font-data text-ink" title={ASSA_TOKEN.address}>
          {short(ASSA_TOKEN.address)}
        </code>
        <button
          type="button"
          onClick={copyAddress}
          className="text-ink-soft hover:text-ink underline-offset-2 transition-colors hover:underline"
        >
          {copied ? "Copied ✓" : "Copy"}
        </button>
        <a
          href={ASSA_BASESCAN}
          target="_blank"
          rel="noopener noreferrer"
          className="text-ink-soft transition-colors hover:text-ink"
        >
          Basescan ↗
        </a>
      </div>

      {/* Add to wallet */}
      <div className="mt-4 flex flex-col items-start gap-1.5">
        <button
          type="button"
          onClick={addToWallet}
          disabled={status === "working"}
          className="btn-ghost gap-2 px-6 text-[14px] font-semibold disabled:opacity-60"
        >
          <img src="/brand/assa-icon.svg" alt="" width="16" height="16" aria-hidden="true" />
          {label}
        </button>
        {status === "nowallet" && (
          <span className="text-ink-soft text-[12px]">No wallet detected — opened metamask.io to install.</span>
        )}
        {status === "rejected" && (
          <span className="text-ink-soft text-[12px]">Cancelled in your wallet.</span>
        )}
        {status === "error" && (
          <span className="text-ink-soft text-[12px]">Couldn’t add automatically — switch to Base and retry.</span>
        )}
      </div>
    </div>
  );
}
